import { useState } from "react";
import { useController } from "react-hook-form";
import { Search, X } from "lucide-react";
import { TextInputStep } from "src/features/vendorSponsorshipAdmin/components/TextInputStep";
import { RadioGroup, RadioGroupItem } from "src/components/ui/radio-group";
import { useHashtags } from "src/features/Hashtags/hooks/useHashtags";
import { useDebounce } from "src/hooks/useDebounce";

export interface LinkStepProps {
	name: string;
	linkName: string;
	feedName: string;
	label: string;
	onOpenSelectFeedModalClick?: () => void;
	className?: string;
}

const LinkStep = ({
	name,
	linkName,
	feedName,
	label,
	...restProps
}: LinkStepProps) => {
	const { field: linkType } = useController({ name });
	const { field: link } = useController({ name: linkName });
	const { field: feed } = useController({ name: feedName });
	const { field: selectedFeed } = useController({ name: "selectedFeed" });

	const [searchTerm, setSearchTerm] = useState("");
	const debouncedSearchTerm = useDebounce(searchTerm, 300);

	const { data } = useHashtags(debouncedSearchTerm, {
		enabled: linkType.value === "FEED"
	});

	const hashtags = data ? data?.pages.flatMap(({ results }) => results) : [];

	const handleLinkTypeChange = (value: string) => {
		if (value === "NONE") {
			linkType.onChange(null);
			selectedFeed.onChange(null);
			feed.onChange(null);
			return;
		}

		if (value === "WEB") {
			selectedFeed.onChange(null);
			feed.onChange(null);
		}

		if (value === "FEED") {
			link.onChange(null);
		}

		linkType.onChange(value);
	};

	const handleFeedSelect = (hashtag: { uuid: string; name: string }) => {
		feed.onChange(hashtag.uuid);
		selectedFeed.onChange(hashtag);
		setSearchTerm("");
	};

	const handleFeedClear = () => {
		feed.onChange(null);
		selectedFeed.onChange(null);
		setSearchTerm("");
	};

	return (
		<div {...restProps}>
			<fieldset className="mb-6">
				<legend className="sr-only">{label}</legend>

				<RadioGroup
					value={linkType.value ?? "NONE"}
					onValueChange={handleLinkTypeChange}
					className="flex flex-row gap-6"
				>
					<div className="flex items-center gap-2">
						<RadioGroupItem value="NONE" id="link-none" />
						<label htmlFor="link-none" className="text-base cursor-pointer">
							None
						</label>
					</div>

					<div className="flex items-center gap-2">
						<RadioGroupItem value="WEB" id="link-web" />
						<label
							htmlFor="link-web"
							className="text-base text-zinc-700 cursor-pointer"
						>
							URL link
						</label>
					</div>

					<div className="flex items-center gap-2">
						<RadioGroupItem value="FEED" id="link-feed" />
						<label htmlFor="link-feed" className="text-base cursor-pointer">
							Social feed #Hashtag link
						</label>
					</div>
				</RadioGroup>
			</fieldset>

			{linkType.value === "WEB" && (
				<TextInputStep
					name={linkName}
					label="Add a URL link"
					placeholder="e.g. google.com"
				/>
			)}

			{linkType.value === "FEED" && (
				<div className="max-w-[480px]">
					{feed.value && selectedFeed.value ? (
						<div>
							<p className="text-sm font-medium text-zinc-600 mb-2">
								Linked feed
							</p>
							<div className="flex items-center gap-2">
								<div className="flex-1 flex items-center bg-white border border-border rounded-lg px-3 py-2 shadow-xs">
									<span className="text-muted-foreground mr-1">#</span>
									<span className="text-sm text-foreground font-medium">
										{selectedFeed.value?.name}
									</span>
								</div>
								<button
									type="button"
									className="h-9 w-9 flex items-center justify-center rounded-lg border border-border bg-white hover:bg-muted transition-colors shadow-xs"
									aria-label="Remove feed link"
									onClick={handleFeedClear}
								>
									<X className="h-4 w-4 text-muted-foreground" />
								</button>
							</div>
						</div>
					) : (
						<div>
							<p className="text-sm font-medium text-zinc-600 mb-2">
								Search for a hashtag feed
							</p>
							<div className="relative">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
								<input
									type="text"
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									placeholder="Search hashtags..."
									className="w-full bg-white border border-border rounded-lg pl-9 pr-4 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
								/>
							</div>

							{hashtags.length > 0 && searchTerm && (
								<ul className="mt-2 bg-white border border-border rounded-lg shadow-md max-h-60 overflow-y-auto">
									{hashtags.map((hashtag) => (
										<li
											key={hashtag.uuid}
											className="px-3 py-2.5 cursor-pointer hover:bg-primary-lighter text-sm text-foreground transition-colors first:rounded-t-lg last:rounded-b-lg"
											onClick={() => handleFeedSelect(hashtag)}
										>
											#{hashtag.name}
										</li>
									))}
								</ul>
							)}

							<p className="text-[13px] text-muted-foreground mt-2">
								Create new # links via the CMS
							</p>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default LinkStep;
